import React, { useState, useEffect } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { FormSection, FormHeader, FormFooter, Field } from '@atlaskit/form';
import { Box, Text } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import {
  useNotification,
  useSidebar,
  NotificationTypes,
  successNotificationBuilder,
  errorNotificationBuilder,
} from '@css-xp/base';
import { AddContactFields, AddContactMultiSelectField, AddContactSelectFields } from '@css-xp/ppc-add-contact-fields';
import { BillingAdmin, ContactInfo } from '@css-xp/graphtypes/src/generated-types-new';
import { useContactServicePayload } from '@css-xp/contact-resources';
import { createContactRelationship, patchContactRelationship } from '@atlassian/temp-proxies';
import { failureMessage } from '@css-xp/ppc-common';
import { CreateContactRelationshipsRequest } from '@css-xp/contact-resources/src/constant';

interface Props {
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  transactionAccountId: string;
  invoiceGroupId: string;
  version: number;
  transactionAccountBillingAdmins?: BillingAdmin[];
  shouldRenderForInternalUse?: boolean;
  isCustomerContactsEditable?: boolean;
  primaryContactDetails?: ContactInfo;
  validPartnerDomains?: string[];
}

export const CustomerContactPane = ({
  invoiceGroupId,
  refetch,
  transactionAccountId,
  version,
  shouldRenderForInternalUse,
  transactionAccountBillingAdmins,
  isCustomerContactsEditable,
  primaryContactDetails,
  validPartnerDomains,
}: Props): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
  const [atlassianAccountIds, setAtlassianAccountIds] = useState<Array<string>>([]);
  const [atlassianAccountEmails, setAtlassianAccountEmails] = useState<Array<string>>(['']);
  const [internalUseContacts, setInternalUseContacts] = useState<BillingAdmin[]>([]);
  const { closeSidebar } = useSidebar();
  const {
    getCreateContactRelationshipsPayload,
    getPatchPayloadForAddingAndRemovingPrimaryContact,
  } = useContactServicePayload();
  const [isInvalidPrimaryContact, setIsInvalidPrimaryContact] = useState<boolean>(false);
  const [isSetAsPrimaryChecked, setIsSetAsPrimaryChecked] = useState<boolean>(false);

  useEffect(() => {
    const hasAtlassianAccountId = atlassianAccountIds.findIndex((id) => !id) >= 0 || isInvalidPrimaryContact;
    setIsSaveDisabled(hasAtlassianAccountId);

    if (shouldRenderForInternalUse && !isCustomerContactsEditable) {
      const hasInternalUseContacts = internalUseContacts?.length > 0;
      setIsSaveDisabled(!hasInternalUseContacts);
    }
  }, [atlassianAccountIds, internalUseContacts, isInvalidPrimaryContact, isCustomerContactsEditable]);

  const { sendSuccessNotification, notificationSent, setNotificationSent, sendErrorNotification } = useNotification();

  const handleFormSubmit = async () => {
    setIsLoading(true);

    try {
      const appropriateAtlassianIds =
        shouldRenderForInternalUse && !isCustomerContactsEditable
          ? internalUseContacts?.map(function (item) {
              return item.atlassianAccountId as string;
            })
          : atlassianAccountIds;

      const createContactPayLoad = getCreateContactRelationshipsPayload({
        invoiceGroupId,
        transactionAccountId: transactionAccountId,
        createContactIds: appropriateAtlassianIds,
        ...(shouldRenderForInternalUse && { createBillingContactIds: appropriateAtlassianIds }),
      });

      const patchContactPayload = getPatchPayloadForAddingAndRemovingPrimaryContact({
        invoiceGroupId: invoiceGroupId,
        transactionAccountId: transactionAccountId,
        newPrimaryContactId: atlassianAccountIds[0],
        existingPrimaryContactId: primaryContactDetails?.accountId as string,
      });

      // First API call: Create contact
      await createContactRelationship(createContactPayLoad as CreateContactRelationshipsRequest);

      // Second API call: Patch contact to set as primary
      if (isSetAsPrimaryChecked) {
        try {
          await patchContactRelationship(patchContactPayload);
        } catch (patchError) {
          const notificationOptions = errorNotificationBuilder()
            .isVersion(NotificationTypes.FLAG)
            .withHeader('Contact partially added')
            .withMessage('Contact was created, but setting it as primary failed.')
            .withDismissAfter(5000)
            .build();
          sendErrorNotification(notificationOptions);
          setNotificationSent(true);
          closeSidebar();
          refetch();
        }
      }

      setIsLoading(false);
      if (!notificationSent) {
        const notificationOptions = successNotificationBuilder()
          .withId('add-billing-profile-customer-contact-success')
          .withHeader('Update successful')
          .withMessage(shouldRenderForInternalUse ? 'Contact added' : 'Customer contact added')
          .withDismissAfter(5000)
          .isVersion(NotificationTypes.FLAG)
          .build();

        sendSuccessNotification(notificationOptions);
        setNotificationSent(true);
        closeSidebar();
        refetch();
      }
    } catch (error) {
      setIsLoading(false);
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Update unsuccessful')
          .withMessage(failureMessage())
          .withDismissAfter(5000)
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
      }
    }
  };

  const customerContactsText = shouldRenderForInternalUse ? 'contacts' : 'customer contacts';
  const customerContactText = shouldRenderForInternalUse ? 'contact' : 'customer contact';
  const customerContactSentenceText = shouldRenderForInternalUse ? 'Contact' : 'Customer contact';

  return (
    <div data-testid="customer-contact-pane">
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title={version === 2 ? '' : 'Add customer contact'}>
              {shouldRenderForInternalUse ? (
                <Text size="small">
                  Your primary point of billing contact for the billing profile, receiving all subscription
                  notifications.
                </Text>
              ) : (
                <Text size="small">
                  You organization’s point of contact who’re in touch with Atlassian billing partners. Also called
                  technical contact.
                </Text>
              )}
            </FormHeader>
            {!isCustomerContactsEditable && (
              <Box paddingBlockStart="space.300">
                <SectionMessage appearance="warning">
                  <p>You can’t remove {customerContactsText} once they’re added.</p>
                </SectionMessage>
              </Box>
            )}
            <FormSection>
              <>
                {shouldRenderForInternalUse ? (
                  <Field label="Contact" name="contact" isRequired>
                    {() =>
                      isCustomerContactsEditable ? (
                        <AddContactSelectFields
                          isExistingContactRemoveDisabled={true}
                          isAddNewContactDisabled={true}
                          setAtlassianAccountIds={setAtlassianAccountIds}
                          atlassianAccountIds={atlassianAccountIds}
                          atlassianAccountEmails={atlassianAccountEmails}
                          setAtlassianAccountEmails={setAtlassianAccountEmails}
                          currentUserList={atlassianAccountIds}
                          userAlreadyExistText="Customer contact already exists"
                          emailFieldText="Select from the list"
                          shouldAllowAddingContact={false}
                          textFieldLabel="Enter email"
                          nameFieldText="Enter contact full name"
                          availableOptions={transactionAccountBillingAdmins?.map(
                            (billingAdmin: BillingAdmin | any) => ({
                              fullName: billingAdmin?.fullName ?? '',
                              email: billingAdmin?.email ?? '',
                              picture: (billingAdmin?.picture as any) ?? '',
                              atlassianAccountId: billingAdmin?.atlassianAccountId ?? '',
                            })
                          )}
                          isRemoveButtonHidden
                          isSetAsPrimaryEnabled={isCustomerContactsEditable}
                          setIsSetAsPrimaryChecked={setIsSetAsPrimaryChecked}
                        />
                      ) : (
                        <AddContactMultiSelectField
                          emailFieldText="Select from the list"
                          fieldType="contact"
                          billingAdmins={transactionAccountBillingAdmins}
                          setBillingAdmins={setInternalUseContacts}
                        />
                      )
                    }
                  </Field>
                ) : (
                  <AddContactFields
                    setAtlassianAccountIds={setAtlassianAccountIds}
                    atlassianAccountIds={atlassianAccountIds}
                    atlassianAccountEmails={atlassianAccountEmails}
                    setAtlassianAccountEmails={setAtlassianAccountEmails}
                    currentUserList={atlassianAccountIds}
                    userAlreadyExistText={`${customerContactSentenceText} already exists`}
                    isExistingContactRemoveDisabled={false}
                    fullNameFieldLabel="Full name"
                    emailFieldText=""
                    nameFieldText=""
                    isAddNewContactDisabled={isCustomerContactsEditable}
                    addButtonText={`Add ${customerContactText}`}
                    emailFieldLabel={`${customerContactSentenceText} email`}
                    version={version}
                    validPartnerDomains={validPartnerDomains}
                    setIsInvalidPrimaryContact={setIsInvalidPrimaryContact}
                    isInvalidPrimaryContact={isInvalidPrimaryContact}
                    isSetAsPrimaryEnabled={isCustomerContactsEditable}
                    setIsSetAsPrimaryChecked={setIsSetAsPrimaryChecked}
                  />
                )}
              </>
            </FormSection>

            <FormFooter>
              <ButtonGroup>
                <Button onClick={closeSidebar}>Cancel</Button>
                <LoadingButton
                  isDisabled={isSaveDisabled}
                  appearance="primary"
                  type="submit"
                  testId="add-customer-contact-button"
                  isLoading={isLoading}
                >
                  Save
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};

export default React.memo(CustomerContactPane);
